import type { User } from '@qse/constants'

interface UserAvatarProps {
  user: User
  size?: 'sm' | 'md' | 'lg' | 'xl'
  className?: string
  showBorder?: boolean
}

const sizeClasses = {
  sm: 'w-8 h-8 text-xs',
  md: 'w-10 h-10 text-sm',
  lg: 'w-12 h-12 text-base',
  xl: 'w-16 h-16 text-lg',
}

const fontSizeClasses = {
  sm: 'text-sm',
  md: 'text-lg',
  lg: 'text-xl',
  xl: 'text-2xl',
}

export const UserAvatar: React.FC<UserAvatarProps> = ({
  user,
  size = 'md',
  className = '',
  showBorder = true,
}) => {
  const firstName = user?.firstName || ''
  const lastName = user?.lastName || ''
  const firstInitial = firstName.charAt(0).toUpperCase()
  const lastInitial = lastName.charAt(0).toUpperCase()

  // Use imageUrl if available, otherwise fall back to avatar
  const profileImageUrl = user?.imageUrl || user?.avatar

  if (profileImageUrl) {
    return (
      <img
        alt={`${user.firstName} ${user.lastName}`}
        src={profileImageUrl}
        className={`rounded-xl object-fill bg-gray-200 dark:bg-[#222] ${sizeClasses[size]} ${
          showBorder ? 'border-2 border-gray-300 dark:border-white/10' : ''
        } ${className}`.trim()}
      />
    )
  }

  return (
    <div
      className={`rounded-xl bg-gray-200 dark:bg-[#222] flex items-center justify-center text-gray-900 dark:text-white font-semibold ${sizeClasses[size]} ${
        showBorder ? 'border-2 border-gray-300 dark:border-white/10' : ''
      } ${className}`.trim()}
    >
      {firstName && lastName ? (
        <div className={`flex items-center justify-center ${fontSizeClasses[size]}`}>
          <span className={` font-bold leading-none`}>{firstInitial}</span>
          <span className={` font-semibold leading-none ml-0.5`}>{lastInitial}</span>
        </div>
      ) : firstName ? (
        <span className={`font-bold leading-none`}>{firstInitial}</span>
      ) : lastName ? (
        <span className={`${fontSizeClasses[size]} font-bold leading-none`}>{lastInitial}</span>
      ) : (
        <span className={`${fontSizeClasses[size]} font-bold leading-none`}>U</span>
      )}
    </div>
  )
}
